import React from 'react';
import { css } from '@emotion/react';
import { CircularProgress, IconButton, Tooltip } from '@material-ui/core';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import Snackbar from '@/components/Snackbar';
import { PrimaryButton } from '@/styles/blocks';
import { TabCard } from '../blocks';
import { TYPE_LABELS } from './constants';
import { useDocuments } from './hooks';
import UploadModal from './UploadModal';
import {
    DocumentMeta,
    DocumentName,
    DocumentRow,
    EmptyState,
    GroupCard,
    GroupHeader,
    Panel,
    PanelHeader,
    PanelSubtitle,
    PanelTitle,
    RowActions,
    ViewLink,
} from './blocks';

const cardPadding = css`
    padding: 2rem;
`;

// The archive card sits below the custom one on the Account pages; master spaced it 2rem off both.
const archivedCardSpacing = css`
    padding: 2rem;
    margin: 2rem 0;
`;

const Row = ({ document, editable, loading, onRemove }) => (
    <DocumentRow>
        <div>
            <DocumentName>{document.name}</DocumentName>
            <DocumentMeta>{TYPE_LABELS[document.type] || document.type || ''}</DocumentMeta>
        </div>
        <RowActions>
            <ViewLink href={document.link} target="_blank" rel="noopener noreferrer">
                View
            </ViewLink>
            {editable && (
                <Tooltip title="Delete" placement="top">
                    <span>
                        <IconButton
                            aria-label={`Delete ${document.name}`}
                            disabled={loading}
                            onClick={() => onRemove(document.document_id)}
                            css={css`
                                padding: 4px;
                            `}
                        >
                            <DeleteForeverIcon fontSize="small" color={loading ? 'disabled' : 'error'} />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </RowActions>
    </DocumentRow>
);

/**
 * The one document panel. `archived` selects which half of the client's documents it owns and
 * whether uploads are filed as archived — that was the only substantive difference between
 * CustomDocuments and ArchiveDocuments, which now both render this.
 *
 * `bare` drops the TabCard surface for callers that already provide one, same contract as
 * CustomTags and TariffTable.
 */
const DocumentPanel = ({
    clientId,
    archived = false,
    isAdmin = false,
    editable = true,
    allowedTypes,
    bare = false,
}) => {
    const { state, callbacks } = useDocuments({ clientId, archived, allowedTypes });
    const { documents, groups, typeOptions, draft, uploading, loading, documentsLoading, valid, notification } = state;

    // The admin read-only panel renders nothing rather than an empty heading.
    if (isAdmin && !editable && documents.length === 0) return null;

    const custom = !archived;
    let title = 'Documents';
    if (!custom) title = isAdmin ? 'Onward Admin Archived Documents' : 'Archived Documents';
    const subtitle = custom
        ? 'Upload documents here to be used throughout the Onward platform.'
        : 'Store Onward-related documents here for your records.';
    const Surface = bare || isAdmin ? React.Fragment : TabCard;
    const surfaceProps = bare || isAdmin ? {} : { css: archived ? archivedCardSpacing : cardPadding };

    return (
        <>
            <Surface {...surfaceProps}>
                <Panel>
                    <PanelHeader>
                        <div>
                            <PanelTitle>{title}</PanelTitle>
                            {editable && <PanelSubtitle>{subtitle}</PanelSubtitle>}
                        </div>
                        {editable && (
                            <PrimaryButton disabled={loading} onClick={callbacks.openUpload}>
                                Upload
                            </PrimaryButton>
                        )}
                    </PanelHeader>

                    {documentsLoading && !documents.length ? (
                        <EmptyState>
                            <CircularProgress size={18} />
                        </EmptyState>
                    ) : (
                        <>
                            {groups.map((group) => (
                                <GroupCard key={group.key}>
                                    <GroupHeader>{group.label}</GroupHeader>
                                    {group.documents.map((document) => (
                                        <Row
                                            key={document.document_id}
                                            document={document}
                                            editable={editable}
                                            loading={loading}
                                            onRemove={callbacks.remove}
                                        />
                                    ))}
                                </GroupCard>
                            ))}
                            {!documents.length && <EmptyState>No documents yet.</EmptyState>}
                        </>
                    )}
                </Panel>
            </Surface>

            {/* The app's Snackbar wrapper, not MUI's: it's the one that takes `handleClose` and `severity`. */}
            <Snackbar
                open={!!notification.message}
                handleClose={callbacks.clearNotification}
                autohide={4000}
                {...notification}
            />

            <UploadModal
                draft={draft}
                uploading={uploading}
                loading={loading}
                valid={valid}
                typeOptions={typeOptions}
                callbacks={callbacks}
            />
        </>
    );
};

export default DocumentPanel;
